import React, { useCallback, useEffect, useRef } from 'react';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredShipments,
  selectShipmentLoading,
  selectShipmentLoadingMore,
  selectShipmentHasMore,
  selectfetchShipmentLoading,
} from './store/selectors';
import { deleteShipment, editShipmentData, loadMoreShipments } from './store/slice';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { all_routes } from '../../router/all_routes';

const ShipmentTable = () => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const { filteredData, isLoading, isLoadingMore, hasMore, isFetchDataLoading } = useSelector(state => ({
    filteredData: selectFilteredShipments(state),
    isLoading: selectShipmentLoading(state),
    isLoadingMore: selectShipmentLoadingMore(state),
    hasMore: selectShipmentHasMore(state),
    isFetchDataLoading: selectfetchShipmentLoading(state),
  }));

  const handleEditClick = record => {
    dispatch(editShipmentData(record.id));
  };

  const handleDeleteClick = id => {
    dispatch(deleteShipment(id));
  };

  const tryLoadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) {
      return;
    }
    dispatch(loadMoreShipments());
  }, [dispatch, hasMore, isLoading, isLoadingMore]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 120) {
        tryLoadMore();
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [tryLoadMore]);

  const isShipments = window.location.pathname == all_routes.shipments;
  const columns = [
    {
      title: 'Date',
      dataIndex: 'shipDate',
      sorter: (a, b) => a.shipDate.length - b.shipDate.length,
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      sorter: (a, b) => a.customerName.length - b.customerName.length,
    },
    {
      title: 'Shipment / ASP #',
      dataIndex: 'asp',
      sorter: (a, b) => a.asp.length - b.asp.length,
    },

    {
      title: 'Packing Slip#',
      dataIndex: 'packingSlipNumber',
      sorter: (a, b) => a.packingSlipNumber.length - b.packingSlipNumber.length,
    },

    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => (
        <span
          style={{
            backgroundColor: record.backgroundColor,
            color: record.textColor,
            padding: '4px 8px',
            borderRadius: '4px',
          }}
        >
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
    },
    {
      title: 'Ship To',
      dataIndex: 'shipTo',
      sorter: (a, b) => a.shipTo.length - b.shipTo.length,
    },
    {
      title: 'Trailer',
      dataIndex: 'trailer',
      sorter: (a, b) => (a.trailer || '').length - (b.trailer || '').length,
    },
    {
      title: 'Carrier',
      dataIndex: 'carrier',
      sorter: (a, b) => (a.carrier || '').localeCompare(b.carrier || ''),
      render: text => text || '—',
    },

    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" onClick={() => handleEditClick(record)}>
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            {!isShipments && (
              <Link className="confirm-text p-2" onClick={() => handleDeleteClick(record.id)}>
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      className="table-responsive product-list"
      ref={tableContainerRef}
      style={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}
    >
      {(isLoading || isFetchDataLoading) && <LoadingSpinner />}
      <Table columns={columns} dataSource={filteredData} pagination={false} rowKey="id" />
      {isLoadingMore && (
        <div
          className="shipment-load-more-loader"
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '14px 16px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderTop: '1px solid #dbe0e6',
            boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-hidden="true"
          />
          <span style={{ fontSize: 13, color: '#475467', fontWeight: 500 }}>
            Loading more shipments...
          </span>
        </div>
      )}
    </div>
  );
};

export default ShipmentTable;
